import type {
  DeanonymizationEvent,
  DeanonymizationProviderReadyEvent,
  ProviderToggle,
} from '../../../shared/types'
import { logger } from '../logger'
import { isAbortError, onAbort } from './abort'
import { deanonymizeWith6Sense, get6SenseGlobal } from './6sense'
import { deanonymizeWithBombora, getBomboraGlobal } from './bombora'
import { subscribeToLiftAi } from './liftai'

type Event = DeanonymizationProviderReadyEvent | DeanonymizationEvent

type Callback = (event: Event) => void

// Batch events within this time frame
const waitMs = 200

export const subscribeToSnippetsDeanonymizationEvents = ({
  providers,
  onEvent,
  signal,
}: {
  providers: Array<ProviderToggle>
  onEvent: Callback
  // Aborting stops the reads: the pollers end, LiftAi's listeners come off, and nothing queued or
  // late is emitted. The consent gate aborts on a denial.
  signal?: AbortSignal
}) => {
  const eventsQueue: DeanonymizationEvent['data'][number][] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  let unsubscribeLiftAi: (() => void) | null = null

  const aborted = (): boolean => signal?.aborted === true

  const emit = (event: Event): void => {
    if (!aborted()) {
      onEvent(event)
    }
  }

  const flush = () => {
    timer = null
    if (!eventsQueue.length) {
      return
    }
    const batch = eventsQueue.splice(0, eventsQueue.length)
    emit({ type: 'Deanonymization', data: batch })
  }

  const add = (event: DeanonymizationEvent['data'][number]) => {
    if (aborted()) {
      return
    }
    eventsQueue.push(event)

    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(flush, waitMs)
  }

  onAbort(signal, () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    eventsQueue.length = 0
    unsubscribeLiftAi?.()
    unsubscribeLiftAi = null
  })

  // Nothing to wire for a subscription that was already cancelled.
  if (aborted()) {
    return
  }

  const sixSenseProvider = providers.find(item => item.provider === '6Sense')
  if (sixSenseProvider) {
    get6SenseGlobal(signal)
      .then(() => {
        emit({ type: 'Deanonymization_Provider_Ready', provider: '6Sense' })
        deanonymizeWith6Sense(signal)
          .then(data => {
            add(data)
          })
          .catch(err => {
            if (!isAbortError(err)) {
              logger.warn('Error in deanonymizeWith6Sense()', err)
            }
          })
      })
      // Intentionally swallowed: provider errors must not propagate to the host site's error handlers.
      .catch(() => {})
  }

  const liftAiProvider = providers.find(item => item.provider === 'LiftAi')
  if (liftAiProvider) {
    // LiftAi doesn't expose a global to wait for; its tag dispatches events
    // directly on `document`. Mark the provider ready immediately and let
    // subscribeToLiftAi emit on each cookie write + catch up if cookies are
    // already populated.
    emit({ type: 'Deanonymization_Provider_Ready', provider: 'LiftAi' })
    try {
      unsubscribeLiftAi = subscribeToLiftAi(data => {
        add(data)
      })
    } catch (err) {
      logger.warn('Error in subscribeToLiftAi()', err)
    }
  }

  const bomboraProvider = providers.find(
    item => item.provider === 'Bombora' || item.poweredBy === 'Bombora'
  )
  if (bomboraProvider) {
    getBomboraGlobal(signal)
      .then(client => {
        const isCPProvider = bomboraProvider.provider === 'ChiliPiper'

        if (Boolean(client._isPoweredByChiliPiper) !== isCPProvider) {
          logger.error(
            `Missmatch between Bombora and ChiliPiper integration configuration. Bombora snippet is powered by ${client._isPoweredByChiliPiper ? 'ChiliPiper' : 'Bombora'} and integration configuration expects it to be powered by ${isCPProvider ? 'ChiliPiper' : 'Bombora'}`
          )
          return
        }

        emit({ type: 'Deanonymization_Provider_Ready', provider: 'Bombora' })
        deanonymizeWithBombora(signal)
          .then(data => {
            add(
              isCPProvider
                ? { provider: 'ChiliPiper', poweredBy: 'Bombora', data: data.data }
                : data
            )
          })
          .catch(err => {
            if (!isAbortError(err)) {
              logger.warn('Error in deanonymizeWithBombora()', err)
            }
          })
      })
      // Intentionally swallowed: provider errors must not propagate to the host site's error handlers.
      .catch(() => {})
  }
}
